import ExcelJS from "exceljs";
import {
  FILE_VERIFICATION_HEADERS,
  getFileVerificationExportRow,
  getSummaryMetricRows,
} from "./aiVerificationReportFormat";

const HEADER_FILL: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF1F3F5" },
};

function styleSectionTitle(row: ExcelJS.Row, colSpan: number): void {
  row.getCell(1).font = { bold: true, size: 12 };
  if (colSpan > 1) {
    row.worksheet.mergeCells(row.number, 1, row.number, colSpan);
  }
}

function styleTableHeaderRow(row: ExcelJS.Row, colCount: number): void {
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c);
    cell.font = { bold: true, size: 11 };
    cell.fill = HEADER_FILL;
    cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFDEE2E6" } },
      left: { style: "thin", color: { argb: "FFDEE2E6" } },
      bottom: { style: "thin", color: { argb: "FFDEE2E6" } },
      right: { style: "thin", color: { argb: "FFDEE2E6" } },
    };
  }
  row.height = 22;
}

function styleDataRow(row: ExcelJS.Row, colCount: number, wrapCol?: number): void {
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c);
    const alignLeft = c === 1 || c === wrapCol;
    cell.alignment = {
      wrapText: true,
      vertical: "top",
      horizontal: alignLeft ? "left" : "center",
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE9ECEF" } },
      left: { style: "thin", color: { argb: "FFE9ECEF" } },
      bottom: { style: "thin", color: { argb: "FFE9ECEF" } },
      right: { style: "thin", color: { argb: "FFE9ECEF" } },
    };
  }
}

async function downloadWorkbook(workbook: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** Summary + file table with wrap text and readable column widths for Excel. */
export async function downloadReportSummaryExcel(
  summary: Record<string, unknown> | null,
  fileDetails: Record<string, unknown>[],
  filename = "system_verification_report_summary.xlsx",
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Verification Report");

  const fileHeaders = [...FILE_VERIFICATION_HEADERS];
  const fileColCount = fileHeaders.length;
  const errorDetailsCol = 9;

  sheet.columns = [
    { width: 14 },
    { width: 28 },
    { width: 16 },
    { width: 14 },
    { width: 12 },
    { width: 12 },
    { width: 14 },
    { width: 16 },
    { width: 52 },
    { width: 20 },
  ];

  let rowNum = 1;
  const metricRows = getSummaryMetricRows(summary);

  if (metricRows.length > 0) {
    styleSectionTitle(sheet.getRow(rowNum), 2);
    sheet.getRow(rowNum).getCell(1).value = "SUMMARY METRICS";
    rowNum++;

    const metricHeader = sheet.getRow(rowNum);
    metricHeader.getCell(1).value = "Metric";
    metricHeader.getCell(2).value = "Value";
    styleTableHeaderRow(metricHeader, 2);
    rowNum++;

    metricRows.forEach(([label, value]) => {
      const row = sheet.getRow(rowNum);
      row.getCell(1).value = label;
      row.getCell(2).value = value as ExcelJS.CellValue;
      styleDataRow(row, 2);
      rowNum++;
    });

    rowNum++;
  }

  styleSectionTitle(sheet.getRow(rowNum), fileColCount);
  sheet.getRow(rowNum).getCell(1).value = "FILE VERIFICATION STATUS";
  rowNum++;

  const fileHeaderRow = sheet.getRow(rowNum);
  fileHeaders.forEach((h, i) => {
    fileHeaderRow.getCell(i + 1).value = h;
  });
  styleTableHeaderRow(fileHeaderRow, fileColCount);
  rowNum++;

  fileDetails.forEach((file) => {
    const values = getFileVerificationExportRow(file);
    const row = sheet.getRow(rowNum);
    values.forEach((v, i) => {
      row.getCell(i + 1).value = v as ExcelJS.CellValue;
    });
    styleDataRow(row, fileColCount, errorDetailsCol);
    const lineCount = String(values[errorDetailsCol - 1] || "").split("\n").filter(Boolean).length;
    if (lineCount > 1) {
      row.height = Math.min(15 * lineCount + 8, 120);
    }
    rowNum++;
  });

  sheet.views = [{ state: "frozen", ySplit: 0, activeCell: "A1" }];

  await downloadWorkbook(workbook, filename);
}

/** @deprecated Use downloadReportSummaryExcel */
export async function downloadReportSummarySampleExcel(
  summary: Record<string, unknown> | null,
  fileDetails: Record<string, unknown>[],
  filename = "system_verification_report_summary_sample.xlsx",
): Promise<void> {
  return downloadReportSummaryExcel(summary, fileDetails, filename);
}
